export const DEFAULT_MAX_DIGITS = 9;

export function resolveMaxDigits(maxDigit) {
  const parsed = Number(maxDigit);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_DIGITS;
}

export function digitCount(value) {
  return value === null || value === undefined ? 0 : String(value).replace(/\D/g, '').length;
}

/**
 * Expands exponent notation into plain decimal digits. Numbers stringify to
 * exponent form once their magnitude reaches 1e21 (or drops below 1e-7), which
 * would otherwise slip past the digit cap unmeasured.
 *
 * The zero padding is bounded by maxDigits: a string such as '1e1000000000'
 * would otherwise ask String.repeat for more characters than a string can hold.
 * Padding one digit past the cap is enough for the caller to see that the value
 * overflows and to read the digits it keeps, so a shorter run of zeroes yields
 * the same result as the full expansion.
 *
 * @param {string|number} value
 * @param {number} maxDigits
 * @returns {string}
 */
function toPlainDecimalString(value, maxDigits) {
  const raw = String(value);
  const parts = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(raw);
  if (!parts) {
    return raw;
  }
  const [, sign, integerDigits, fractionDigits = '', exponent] = parts;
  const digits = integerDigits + fractionDigits;
  const pointIndex = integerDigits.length + Number(exponent);
  const padLimit = Math.max(maxDigits, 0);
  if (pointIndex <= 0) {
    return `${sign}0.${'0'.repeat(Math.min(-pointIndex, padLimit))}${digits}`;
  }
  if (pointIndex >= digits.length) {
    const zeros = Math.min(pointIndex - digits.length, Math.max(padLimit + 1 - digits.length, 0));
    return `${sign}${digits}${'0'.repeat(zeros)}`;
  }
  return `${sign}${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
}

/**
 * Trims anything typed or pasted beyond maxDigits digits.
 *
 * @param {string|number|null|undefined} value
 * @param {number} maxDigits
 * @returns {string|number}
 */
export function limitDigits(value, maxDigits) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const parts = /^(-?)(\d*)(?:\.(\d*))?$/.exec(toPlainDecimalString(value, maxDigits));
  if (!parts) {
    return value;
  }
  const [, sign, integerDigits, fractionDigits = ''] = parts;
  if (integerDigits.length + fractionDigits.length <= maxDigits) {
    return value;
  }
  // Trim the fraction first so the magnitude of the number is never altered.
  const allowedFractionDigits = Math.max(maxDigits - integerDigits.length, 0);
  if (!allowedFractionDigits) {
    return Number(`${sign}${integerDigits.slice(0, maxDigits)}`);
  }
  return Number(`${sign}${integerDigits}.${fractionDigits.slice(0, allowedFractionDigits)}`);
}

/**
 * Decides whether a keypress should be swallowed to keep a numeric input within
 * its digit cap. Only digit keys are ever blocked, so backspace, delete,
 * navigation and clipboard shortcuts keep working at the cap. The check runs
 * against the value the input would hold after the key is applied, so a digit
 * replacing a selection is allowed whenever the replacement still fits.
 */
export function shouldBlockDigitKey(event, value, maxDigits) {
  if (!/^\d$/.test(event.key)) {
    return false;
  }

  const { target } = event;
  const currentValue = typeof target?.value === 'string' ? target.value : String(value ?? '');
  const selectionStart = target?.selectionStart ?? currentValue.length;
  const selectionEnd = target?.selectionEnd ?? currentValue.length;
  const nextValue = currentValue.slice(0, selectionStart) + event.key + currentValue.slice(selectionEnd);

  return digitCount(nextValue) > maxDigits;
}
